import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTools, Divider } from '@treezor/design-system';

import { SummaryInformation } from '@/ui-kit/SummaryInformation/SummaryInformation';

import { CreateUserFooter } from '@/features/user/components/CreateUser/layout/CreateUserFooter';
import { Parent } from '@/features/user/components/Parent';
import { useCreateUser } from '@/features/user/hooks/createUser/useCreateUser';
import { useAddTaxResidences } from '@/features/user/hooks/createUser/useTaxResidences';
import { AddressSummary } from '@/features/user/components/CreateUser/AddressSummary';
import { TaxResidenceSummary } from '@/features/user/components/CreateUser/TaxResidenceSummary';
import { KycDocumentSummary } from '@/features/user/components/CreateUser/KycDocumentSummary';
import { PersonalSituationSummary } from '@/features/user/components/CreateUser/PersonalSituationSummary';
import { EParentType, UserTypes } from '@/features/user/store/user.model';
import { useCreateUserKycDocuments } from '@/features/user/hooks/createUser/useCreateUserKycDocuments';
import { ICreateUserPartProps } from '@/features/user/store/createUser.types';

export function CreateUserPhysicalSummary({ stepsLength }: ICreateUserPartProps) {
  const [userId, setUserId] = useState<number | undefined>();
  const { t } = useTranslation();
  const { config, distributionCountry, user, onCreateUser, isLoading, language, navigateDashboard, parentUser } =
    useCreateUser();

  const { isTaxResidenceCreated, addTaxResidences, isTaxResidenceLoading } = useAddTaxResidences(userId);
  const { addKycDocuments, isKycDocumentLoading, isKycDocumentsUploaded } = useCreateUserKycDocuments(userId);

  const onConfirmCreation = useCallback(() => {
    const { birthday, middleName1, middleName2, taxResidences, specifiedUSPerson, kycDocuments, ...rest } = user;
    const middleNames = middleName1 || middleName2 ? [middleName1, middleName2].filter(Boolean).join(', ') : '';
    const birthdayApiFormat = birthday
      ? DateTools.format(DateTools.localStringToDate(birthday, language), 'api-date')
      : undefined;

    onCreateUser(
      {
        ...rest,
        middleNames,
        birthday: birthdayApiFormat,
        specifiedUSPerson: Number(specifiedUSPerson),
      },
      undefined,
      (userId: number) => {
        setUserId(userId);
        addTaxResidences(taxResidences, userId);
        addKycDocuments(kycDocuments, userId);
      },
    );
  }, [language, onCreateUser, user, addTaxResidences, addKycDocuments]);

  useEffect(() => {
    if (userId && isTaxResidenceCreated && isKycDocumentsUploaded) {
      navigateDashboard(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isTaxResidenceCreated, isKycDocumentsUploaded]);

  return (
    <div className="flex flex-col gap-6">
      <SummaryInformation
        title={t('pages.dashboard.createUser.form.pages.userInformation')}
        sections={[
          ...(!!parentUser
            ? [
                {
                  subTitle: t('pages.dashboard.createUser.form.blocks.hierarchicalRelation'),
                  categories: [
                    {
                      component: <Parent parentUser={parentUser} redirectToParent={navigateDashboard} />,
                    },
                  ],
                },
              ]
            : []),
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.mainInformation'),
            categories: [
              {
                items: [
                  { label: t('pages.dashboard.createUser.form.title'), value: user?.title || '-' },
                  { label: t('pages.dashboard.createUser.form.lastname'), value: user.lastname },
                  {
                    label: t('pages.dashboard.createUser.form.firstname'),
                    value: [user.firstname, user.middleName1, user.middleName2].filter(Boolean).join(', '),
                  },
                  { label: t('pages.dashboard.createUser.form.email'), value: user.email },
                  ...(parentUser?.userTypeId === UserTypes.business
                    ? [
                        {
                          label: t('pages.dashboard.createUser.form.role'),
                          value: user.roles
                            ?.map((role) =>
                              role === EParentType.shareholder
                                ? `${t(`pages.dashboard.createUser.form.roles.shareholderHolding`)} ${user.effectiveBeneficiary}%`
                                : t(`pages.dashboard.createUser.form.roles.${role}`),
                            )
                            ?.join(',\n'),
                        },
                      ]
                    : []),
                  distributionCountry?.length > 0 && {
                    label: t('pages.currentProfiles.profile.informations.distributionCountry'),
                    value: t(`shared.countries.${user.distributionCountry}`),
                  },
                ],
              },
            ],
          },
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.address'),
            categories: [
              {
                component: (
                  <div className="flex gap-8">
                    <AddressSummary
                      isPrimary
                      title={t('pages.dashboard.createUser.form.mainAddress')}
                      address1={user.address1}
                      address2={user.address2}
                      address3={user.address3}
                      postcode={user.postcode}
                      city={user.city}
                      country={user.country}
                    />

                    <AddressSummary
                      title={t('pages.dashboard.createUser.form.secondaryAddress')}
                      address1={user.secondaryAddress1}
                      address2={user.secondaryAddress2}
                      address3={user.secondaryAddress3}
                      postcode={user.secondaryPostcode}
                      city={user.secondaryCity}
                      country={user.secondaryCountry}
                    />
                  </div>
                ),
              },
            ],
          },
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.contact'),
            categories: [
              {
                items: [
                  { label: t('pages.dashboard.createUser.form.phone'), value: user?.phone || '-' },
                  { label: t('pages.dashboard.createUser.form.mobile'), value: user?.mobile || '-' },
                ],
              },
            ],
          },
        ]}
        key="userInformation"
      />
      <SummaryInformation
        title={t('pages.dashboard.createUser.form.pages.verificationDetails')}
        sections={[
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.birthNationality'),
            categories: [
              {
                items: [
                  { label: t('pages.dashboard.createUser.form.birthday'), value: user?.birthday || '-' },
                  {
                    label: t('pages.dashboard.createUser.form.birthCountry'),
                    value: user?.birthCountry ? t(`shared.countries.${user.birthCountry}`) : '-',
                  },
                  { label: t('pages.dashboard.createUser.form.placeOfBirth'), value: user.placeOfBirth || '-' },
                  {
                    label: t('pages.dashboard.createUser.form.nationalities'),
                    value:
                      [user?.nationality, user?.nationalityOther]
                        .filter(Boolean)
                        .map((nationality) => t(`shared.countries.${nationality}`))
                        .join(', ') || '-',
                  },
                ],
              },
            ],
          },
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.situation'),
            categories: [
              {
                ...(config?.settings?.users?.dataQuality?.includes('personalSituation')
                  ? {
                      component: (
                        <PersonalSituationSummary
                          occupationCategory={user.occupationCategory}
                          monthlyIncomeRange={user.monthlyIncomeRange}
                          personalAssetsRange={user.personalAssetsRange}
                        />
                      ),
                    }
                  : {
                      items: [
                        {
                          label: t('pages.dashboard.createUser.form.personalAssets.title'),
                          value: user?.personalAssets
                            ? t(`pages.dashboard.createUser.form.personalAssets.${user.personalAssets}`)
                            : '-',
                        },
                        {
                          label: t('pages.dashboard.createUser.form.incomeRange.title'),
                          value: user?.incomeRange
                            ? t(`pages.dashboard.createUser.form.incomeRange.${user.incomeRange}`)
                            : '-',
                        },
                        {
                          label: t('pages.currentProfiles.profile.financial.occupationType'),
                          value: user?.occupationType
                            ? t(`pages.currentProfiles.profile.financial.occupationTypes.${user.occupationType}`)
                            : '-',
                        },
                      ],
                    }),
              },
            ],
          },

          {
            subTitle: t('pages.currentProfiles.profile.taxResidence.cardTitle'),
            categories: [
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.usTaxResident'),
                    value:
                      Number(user?.specifiedUSPerson || 0) === 1
                        ? t('translation:components.molecules.forms.yes')
                        : t('translation:components.molecules.forms.no'),
                  },
                ],
              },
              {
                component: <TaxResidenceSummary taxResidences={user?.taxResidences} />,
              },
            ],
          },
          ...(user?.kycDocuments?.length > 0
            ? [
                {
                  subTitle: t('pages.dashboard.createUser.form.blocks.documents'),
                  categories: [
                    {
                      component: <KycDocumentSummary documents={user.kycDocuments} />,
                    },
                  ],
                },
              ]
            : []),
        ]}
        key="verificationDetails"
      />
      <Divider />
      <CreateUserFooter
        onNext={onConfirmCreation}
        nextTitle={t('pages.dashboard.createUser.form.actions.confirmCreation')}
        isLoading={isLoading || isTaxResidenceLoading || isKycDocumentLoading}
        stepsLength={stepsLength}
      />
    </div>
  );
}
